/**
 * TimeOff Utility Functions
 * Helper functions for TimeOff component
 */

import moment from "moment-timezone";

/**
 * Calculate number of days between start and end dates/times
 * @param {string} startDate - Start date string
 * @param {number} startTime - Start time in hours (decimal)
 * @param {string} endDate - End date string
 * @param {number} endTime - End time in hours (decimal)
 * @returns {string} Formatted days string
 */
export const calculateNumberOfDays = (startDate, startTime, endDate, endTime) => {
  try {
    if (!startTime || !endTime || startDate !== endDate) {
      const startDateTime = moment(startDate).tz(global.config.getTimeZone()).startOf('day');
      const endDateTime = moment(endDate).tz(global.config.getTimeZone()).startOf('day');
      const duration = moment.duration(endDateTime.diff(startDateTime));
      const numberOfDays = Math.ceil(duration.asDays());
      return `${numberOfDays + 1} days`;
    } else {
      const startDateTime = moment(startDate).tz(global.config.getTimeZone()).startOf('day').add(startTime, 'hours');
      const endDateTime = moment(endDate).tz(global.config.getTimeZone()).startOf('day').add(endTime, 'hours');
      const duration = moment.duration(endDateTime.diff(startDateTime));
      const numberOfDays = Math.ceil(duration.asDays());

      if (numberOfDays === 1) {
        const numberOfHours = (endTime - startTime) * numberOfDays;
        const numberOfDaysDecimal = (numberOfHours / 8).toFixed(2);
        const cappedNumberOfDaysDecimal = Math.min(numberOfDaysDecimal, 1);
        return `${cappedNumberOfDaysDecimal} days`;
      } else {
        return `${numberOfDays} days`;
      }
    }
  } catch (error) {
    console.error("Error calculating number of days:", error);
    return "0 days";
  }
};

/**
 * Calculate days between two dates
 * @param {string|Date} createdAt - Creation date
 * @param {string|Date} startDate - Start date
 * @returns {number} Number of days between dates
 */
export const calculateDaysBetweenDates = (createdAt, startDate) => {
  try {
    const createdDate = new Date(createdAt);
    const start = new Date(startDate);

    if (createdDate < start) {
      const differenceInTime = start - createdDate;
      const differenceInDays = Math.ceil(differenceInTime / (1000 * 60 * 60 * 24));
      return differenceInDays;
    } else {
      return 0;
    }
  } catch (error) {
    console.error("Error calculating days between dates:", error);
    return 0;
  }
};

/**
 * Convert time value to Excel format (HH:MM AM/PM)
 * @param {number} time - Time value in hours (decimal)
 * @returns {string} Formatted time string
 */
export const excelTimeConverter = (time) => {
  try {
    if (typeof time !== 'number' || isNaN(time)) {
      throw new Error("Invalid time format. Expected a number.");
    }

    let hour = parseInt(time);
    let hours = hour;
    let minute = Math.round((time - hour) * 60);

    if (hour > 12) {
      hour -= 12;
    }

    if (minute === 0) {
      minute = "0";
    }

    const formattedHour = hour < 10 ? "0" + hour : hour;
    const formattedMinute = minute < 10 ? "0" + minute : minute;
    const period = hours < 12 ? "am" : "pm";

    return `${formattedHour}:${formattedMinute} ${period}`;
  } catch (error) {
    console.error("Error in excelTimeConverter:", error.message);
    return "--:--";
  }
};

/**
 * Format user name from user object
 * @param {Object} user - User object
 * @returns {string} Formatted user name
 */
export const formatUserName = (user) => {
  if (!user) return "";
  
  if (user.first_name) {
    return `${user.first_name} ${user.middle_name && user.middle_name !== "null" ? user.middle_name + " " : ""}${user.last_name || ""}`.trim();
  } else if (user.company_first_name) {
    return `${user.company_first_name} ${user.company_last_name || ""}`.trim();
  } else if (user.company_name) {
    return `${user.company_name} (Company)`;
  }
  
  return "";
};

/**
 * Format date for display
 * @param {string|Date} date - Date to format
 * @param {string} format - Moment format string (default: "DD/MM/YYYY")
 * @returns {string} Formatted date string
 */
export const formatDate = (date, format = "DD/MM/YYYY") => {
  try {
    if (!date) return "N/A";
    return moment.tz(moment(date).format("YYYY-MM-DD"), global.config.getTimeZone()).format(format);
  } catch (error) {
    console.error("Error formatting date:", error);
    return "N/A";
  }
};

/**
 * Get current date/time formatted for Excel export
 * @returns {Object} Object with formattedDate and formattedTime
 */
export const getExcelDateTime = () => {
  const now = new Date();
  const formattedDate =
    now.getFullYear() +
    "-" +
    (now.getMonth() + 1).toString().padStart(2, "0") +
    "-" +
    now.getDate().toString().padStart(2, "0");
  const formattedTime =
    now.getHours().toString().padStart(2, "0") +
    ":" +
    now.getMinutes().toString().padStart(2, "0") +
    ":" +
    now.getSeconds().toString().padStart(2, "0");
  
  return { formattedDate, formattedTime };
};

/**
 * Check if user has admin privileges
 * @param {number} userType - User type number
 * @returns {boolean} True if user is admin or super admin
 */
export const isAdmin = (userType) => {
  return userType === 1 || userType === 2;
};

/**
 * Check if user can view all users
 * @param {number} userType - User type number
 * @returns {boolean} True if user can view all users
 */
export const canViewAllUsers = (userType) => {
  return userType !== 3 && userType !== 5 && userType !== 4 && userType !== 7;
};
